import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { message } from "antd";
import { type AxiosError } from "axios";

import { type ConfirmPaymentDocumentIssuance } from "~/modules/Products/hooks/useConfirmManualPayment";

import {
  type CollectionsBankTransaction,
  type CollectionsBankTransactionStatus,
} from "@fitness-app/data-models/entities/CollectionsBankTransaction";
import {
  type CollectionsAutomationSettings,
  type CollectionsCallOutcome,
  type CollectionsCase,
  type CollectionsCaseContract,
  type CollectionsCaseStatus,
  type CollectionsEvent,
  type CollectionsMessageChannel,
  type CollectionsSignature,
  type CollectionsSituation,
  type CollectionsStep,
  type CollectionsTask,
  type CollectionsTaskType,
} from "@fitness-app/data-models/entities/CollectionsCase";
import { type ClientInvoiceData } from "@fitness-app/data-models/entities/Order";

import { productApi } from "~/store/initializeStore";

export interface CollectionsCaseListItem extends CollectionsCase {
  clientName: string | null;
  nextTask: Pick<CollectionsTask, "id" | "type" | "dueAt"> | null;
}

export interface CollectionsDashboard {
  dueTasks: { type: CollectionsTaskType; count: number }[];
  overdueAmount: number;
  futureAmount: number;
  overdueCases: number;
  settlementAmount: number;
  externalCollectionAmount: number;
  recoveredThisMonth: number;
  transfersToVerify: number;
  queue: CollectionsCaseListItem[];
  /** Przełączniki automatycznych wiadomości ustawione przez właściciela (TRE-2169). */
  automation?: CollectionsAutomationSettings;
  /** Kto podpisuje maile, SMS-y i pisma (domyślne z konfiguracji uzupełnione). */
  signature?: CollectionsSignature | null;
}

export interface CollectionsInvoice {
  orderId: string;
  invoiceId: string;
  productId: string;
  productClientId: string;
  clientStatus: "active" | "archived";
  paymentCycle: number | null;
  amount: number;
  dueAt: string;
  source: "manual_invoice" | "manual_upcoming" | "card_exhausted" | "card_retrying";
  orderNumber: number | null;
  nextPaymentAttemptAt: string | null;
}

export interface CollectionsCaseContractDetails extends CollectionsCaseContract {
  productName: string | null;
  clientStatus: "active" | "archived";
  installments: number | null;
  cycleNumber: number | null;
  installmentEndsAt: string | null;
  installmentAmount: number;
  invoices: CollectionsInvoice[];
  client: ClientInvoiceData | null;
}

export interface CollectionsCaseDetails {
  case: CollectionsCase;
  clientName: string | null;
  clientPhone: string | null;
  contracts: CollectionsCaseContractDetails[];
  events: CollectionsEvent[];
  tasks: CollectionsTask[];
  recoveredAmount: number;
}

export type CollectionsCaseAction =
  | { type: "status"; status: CollectionsCaseStatus; note?: string }
  | { type: "situation"; situation: CollectionsSituation | null; note?: string }
  | { type: "owner"; ownerId: string | null }
  | { type: "declaration"; amount: number; dueAt: string; note?: string }
  | { type: "settlement"; installments: number; amount: number; changeId?: string | null; note?: string }
  | { type: "agency"; agency: string; reference?: string; note?: string }
  | { type: "note"; note: string };

const KEYS = {
  dashboard: ["collections", "dashboard"] as const,
  cases: (params: unknown) => ["collections", "cases", params] as const,
  case: (caseId: string) => ["collections", "case", caseId] as const,
};

export const useCollectionsDashboard = () =>
  useQuery<CollectionsDashboard, AxiosError>({
    queryKey: KEYS.dashboard,
    staleTime: 60 * 1000,
    queryFn: async () => (await productApi.get<CollectionsDashboard>("/collections/dashboard")).data,
  });

export const useCollectionsCases = (params: { statuses?: CollectionsCaseStatus[]; query?: string }) =>
  useQuery<CollectionsCaseListItem[], AxiosError>({
    queryKey: KEYS.cases(params),
    staleTime: 60 * 1000,
    keepPreviousData: true,
    queryFn: async () =>
      (
        await productApi.get<CollectionsCaseListItem[]>("/collections/cases", {
          params: { statuses: params.statuses?.join(","), query: params.query || undefined, limit: 500 },
        })
      ).data,
  });

export const useCollectionsCase = (caseId: string | undefined) =>
  useQuery<CollectionsCaseDetails, AxiosError>({
    queryKey: KEYS.case(caseId ?? ""),
    enabled: Boolean(caseId),
    staleTime: 30 * 1000,
    queryFn: async () => (await productApi.get<CollectionsCaseDetails>(`/collections/cases/${caseId}`)).data,
  });

const useInvalidateCollections = () => {
  const queryClient = useQueryClient();
  return (caseId?: string) => {
    void queryClient.invalidateQueries(["collections"]);
    if (caseId) {
      void queryClient.invalidateQueries(KEYS.case(caseId));
    }
  };
};

export const useCompleteCollectionsTask = () => {
  const invalidate = useInvalidateCollections();
  const key = "completeCollectionsTask";

  return useMutation({
    mutationFn: async (payload: {
      caseId: string;
      taskId: string;
      outcome?: CollectionsCallOutcome | string | null;
      note?: string;
    }) => {
      message.loading({ content: "Zapisywanie...", key });
      const { caseId, taskId, ...body } = payload;
      return (await productApi.post(`/collections/cases/${caseId}/tasks/${taskId}/complete`, body)).data;
    },
    onSuccess: (_, payload) => {
      message.destroy(key);
      void message.success("Zadanie zamknięte");
      invalidate(payload.caseId);
    },
    onError: () => {
      message.destroy(key);
      void message.error("Nie udało się zamknąć zadania");
    },
  });
};

export const useRecordCollectionsAction = () => {
  const invalidate = useInvalidateCollections();
  const key = "recordCollectionsAction";

  return useMutation({
    mutationFn: async (payload: { caseId: string; action: CollectionsCaseAction }) => {
      message.loading({ content: "Zapisywanie...", key });
      return (await productApi.post(`/collections/cases/${payload.caseId}/actions`, payload.action)).data;
    },
    onSuccess: (_, payload) => {
      message.destroy(key);
      void message.success("Zapisane");
      invalidate(payload.caseId);
    },
    onError: () => {
      message.destroy(key);
      void message.error("Nie udało się zapisać");
    },
  });
};

export const useSyncCollectionsCases = () => {
  const invalidate = useInvalidateCollections();
  const key = "syncCollectionsCases";

  return useMutation({
    mutationFn: async () => {
      message.loading({ content: "Aktualizowanie spraw...", key, duration: 0 });
      return (
        await productApi.post<{ opened: number; reopened: number; updated: number; closed: number; cases: number }>(
          "/collections/sync",
        )
      ).data;
    },
    onSuccess: (summary) => {
      message.destroy(key);
      void message.success(
        `Sprawy zaktualizowane: ${summary.opened} nowych, ${summary.reopened} wznowionych, ${summary.closed} zamkniętych`,
      );
      invalidate();
    },
    onError: () => {
      message.destroy(key);
      void message.error("Nie udało się zaktualizować spraw");
    },
  });
};

export interface PlannedAutomationAction {
  caseId: string | null;
  clientEmail: string;
  step: CollectionsStep | null;
  channels: CollectionsMessageChannel[];
  tasks: CollectionsTaskType[];
  status: CollectionsCaseStatus | null;
  hold: string | null;
  note?: string;
}

export interface AutomationRunSummary {
  settings: CollectionsAutomationSettings;
  today: string;
  dryRun: boolean;
  cases: number;
  sent: Partial<Record<CollectionsStep, number>>;
  tasks: number;
  holds: Record<string, number>;
  bookkeeping: { declarationsKept: number; declarationsBroken: number; settlementsBroken: number; reviews: number };
  actions: PlannedAutomationAction[];
}

// ---------- Przelewy z banku (TRE-2172) ----------

export interface ImportBankCsvSummary {
  importId: string;
  rowsTotal: number;
  rowsCredits: number;
  rowsNew: number;
  rowsDuplicate: number;
  skippedDebits: number;
  skippedInvalid: number;
  proposed: number;
  unmatched: number;
}

export const useBankTransactions = (statuses: CollectionsBankTransactionStatus[]) =>
  useQuery({
    queryKey: ["collections", "bank", statuses],
    queryFn: async () =>
      (
        await productApi.get<CollectionsBankTransaction[]>("/collections/bank/transactions", {
          params: { statuses: statuses.join(","), limit: 500 },
        })
      ).data,
    keepPreviousData: true,
  });

export const useImportBankCsv = () => {
  const invalidate = useInvalidateCollections();
  const key = "importBankCsv";

  return useMutation({
    mutationFn: async (payload: { fileName: string; content: string }) => {
      message.loading({ content: "Wczytywanie wyciągu...", key, duration: 0 });
      return (await productApi.post<ImportBankCsvSummary>("/collections/bank/import", payload)).data;
    },
    onSuccess: (summary) => {
      message.destroy(key);
      void message.success(
        `Wczytano ${summary.rowsNew} nowych przelewów (${summary.proposed} dopasowanych, ${summary.unmatched} do przypisania)` +
          (summary.rowsDuplicate ? `, ${summary.rowsDuplicate} już było` : ""),
        6,
      );
      invalidate();
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      message.destroy(key);
      void message.error(error.response?.data?.message ?? "Nie udało się wczytać wyciągu", 8);
    },
  });
};

export const useSettleBankTransaction = () => {
  const invalidate = useInvalidateCollections();
  const key = "settleBankTransaction";

  return useMutation({
    mutationFn: async (payload: {
      transactionId: string;
      orderId: string;
      productId: string;
      clientEmail: string;
      documentIssuance?: ConfirmPaymentDocumentIssuance;
    }) => {
      message.loading({ content: "Księgowanie...", key, duration: 0 });
      const { transactionId, ...body } = payload;
      return (await productApi.post(`/collections/bank/transactions/${transactionId}/settle`, body)).data;
    },
    onSuccess: () => {
      message.destroy(key);
      void message.success("Zaksięgowane");
      invalidate();
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      message.destroy(key);
      void message.error(error.response?.data?.message ?? "Nie udało się zaksięgować", 8);
    },
  });
};

export const useIgnoreBankTransaction = () => {
  const invalidate = useInvalidateCollections();
  const key = "ignoreBankTransaction";

  return useMutation({
    mutationFn: async (payload: { transactionId: string; reason?: string | null; reopen?: boolean }) => {
      message.loading({ content: "Zapisywanie...", key, duration: 0 });
      const { transactionId, ...body } = payload;
      return (await productApi.post(`/collections/bank/transactions/${transactionId}/ignore`, body)).data;
    },
    onSuccess: (_, payload) => {
      message.destroy(key);
      void message.success(payload.reopen ? "Przelew wrócił do kolejki" : "Pominięty");
      invalidate();
    },
    onError: () => {
      message.destroy(key);
      void message.error("Nie udało się zapisać");
    },
  });
};

/** Podpis wiadomości windykacyjnych (PUT /collections/settings/signature, tylko owner). */
export const useUpdateCollectionsSignature = () => {
  const invalidate = useInvalidateCollections();

  return useMutation({
    mutationFn: async (signature: CollectionsSignature) =>
      (await productApi.put<CollectionsSignature>("/collections/settings/signature", signature)).data,
    onSuccess: () => {
      void message.success("Podpis zapisany");
      invalidate();
    },
    onError: () => void message.error("Nie udało się zapisać podpisu"),
  });
};

/** Przełączniki właściciela: co automat może wysyłać (PUT /collections/settings, tylko owner). */
export const useUpdateCollectionsSettings = () => {
  const invalidate = useInvalidateCollections();
  const key = "updateCollectionsSettings";

  return useMutation({
    mutationFn: async (settings: CollectionsAutomationSettings) => {
      message.loading({ content: "Zapisywanie...", key, duration: 0 });
      return (await productApi.put<CollectionsAutomationSettings>("/collections/settings", settings)).data;
    },
    onSuccess: () => {
      message.destroy(key);
      void message.success("Ustawienia zapisane");
      invalidate();
    },
    onError: () => {
      message.destroy(key);
      void message.error("Nie udało się zapisać ustawień");
    },
  });
};

/** Dzisiejsza wysyłka: na sucho (podgląd, nic nie wychodzi) albo naprawdę. */
export const useRunCollectionsAutomation = () => {
  const invalidate = useInvalidateCollections();
  const key = "runCollectionsAutomation";

  return useMutation({
    mutationFn: async (payload: { dryRun: boolean }) => {
      message.loading({
        content: payload.dryRun ? "Sprawdzanie, co dziś wyjdzie..." : "Wysyłanie dzisiejszych wiadomości...",
        key,
        duration: 0,
      });
      return (await productApi.post<AutomationRunSummary>("/collections/automation/run", payload)).data;
    },
    onSuccess: (summary) => {
      message.destroy(key);
      if (!summary.dryRun) {
        const sent = Object.values(summary.sent).reduce((sum, count) => sum + (count ?? 0), 0);
        void message.success(`Wysłano ${sent} wiadomości, utworzono ${summary.tasks} zadań`);
        invalidate();
      }
    },
    onError: () => {
      message.destroy(key);
      void message.error("Nie udało się wysłać wiadomości");
    },
  });
};

/** Pobiera pismo wezwania jako PDF i otwiera je w nowej karcie. */
export const useDownloadCollectionsLetter = () => {
  const key = "collectionsLetter";

  return useMutation({
    mutationFn: async (payload: { caseId: string; kind: "demand" | "final" }) => {
      message.loading({ content: "Generowanie pisma...", key, duration: 0 });
      const response = await productApi.get<Blob>(`/collections/cases/${payload.caseId}/letter`, {
        params: { kind: payload.kind },
        responseType: "blob",
      });
      return response.data;
    },
    onSuccess: (blob) => {
      message.destroy(key);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    },
    onError: () => {
      message.destroy(key);
      void message.error("Nie udało się wygenerować pisma");
    },
  });
};

export type CollectionsAssistantKind = "email" | "sms" | "call_script" | "summary";
export type CollectionsPriority = "high" | "medium" | "low";

export type CollectionsAssistantDraft =
  | {
      kind: "email";
      draft: {
        subject: string;
        body: string;
        rationale: string;
        priority: CollectionsPriority;
        priorityReason: string;
      };
    }
  | { kind: "sms"; draft: { text: string; rationale: string; priority: CollectionsPriority; priorityReason: string } }
  | {
      kind: "call_script";
      draft: {
        opening: string;
        questions: string[];
        objections: { objection: string; answer: string }[];
        proposedSolution: string;
        closing: string;
        priority: CollectionsPriority;
        priorityReason: string;
      };
    }
  | {
      kind: "summary";
      draft: {
        summary: string;
        recommendation: string;
        risks: string[];
        priority: CollectionsPriority;
        priorityReason: string;
      };
    };

export interface CollectionsAssistantResponse {
  result: CollectionsAssistantDraft;
  model: string;
  usage: { inputTokens: number; outputTokens: number; costUsd: number | null };
  brief: string;
}

/** Draft from the collections assistant (TRE-2171) — nothing is sent until the person clicks „Wyślij". */
export const useDraftCollectionsAssistant = () =>
  useMutation({
    mutationFn: async (payload: { caseId: string; kind: CollectionsAssistantKind; instructions?: string }) =>
      (
        await productApi.post<CollectionsAssistantResponse>(`/collections/cases/${payload.caseId}/assistant`, {
          kind: payload.kind,
          instructions: payload.instructions || undefined,
        })
      ).data,
    onError: (error: AxiosError<{ message?: string }>) => {
      void message.error(
        error.response?.data?.message?.includes("not configured")
          ? "Asystent nie jest skonfigurowany (brak klucza OpenAI)"
          : "Asystent nie odpowiedział",
      );
    },
  });

export const useSendCollectionsCustomMessage = () => {
  const invalidate = useInvalidateCollections();
  const key = "sendCollectionsCustomMessage";

  return useMutation({
    mutationFn: async (payload: { caseId: string; channel: "email" | "sms"; subject?: string; body: string }) => {
      message.loading({
        content: payload.channel === "sms" ? "Wysyłanie SMS-a..." : "Wysyłanie maila...",
        key,
        duration: 0,
      });
      const { caseId, ...body } = payload;
      return (await productApi.post<{ status: string }>(`/collections/cases/${caseId}/messages`, body)).data;
    },
    onSuccess: (_, payload) => {
      message.destroy(key);
      void message.success(payload.channel === "sms" ? "SMS wysłany" : "Mail wysłany");
      invalidate(payload.caseId);
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      message.destroy(key);
      const reason = error.response?.data?.message ?? "";
      void message.error(
        reason.includes("no_phone")
          ? "Klientka nie ma numeru telefonu"
          : reason.includes("outside_window")
            ? "SMS-y wychodzą tylko między 9:00 a 19:00"
            : "Nie udało się wysłać wiadomości",
      );
    },
  });
};
